<template>
    <div style="color: #050F2D;">
        <div class="text-center mb-5"><alert-icon usage="success" /></div>
        <div class="text-center" style="font-size: 20px; font-weight: 500; margin-bottom: 12px;">Intervention Created!</div>
        <div class="text-center" style="margin-bottom: 32px;">&ldquo;{{ interventionName }}&rdquo; was created successfully.</div>
        <div class="border" style="border-radius:10px; padding: 16px 10px 16px 16px;" v-if="reminderAvailable">
            <div class="d-flex">
                <i class="fal fa-envelope-open-text mr-3" style="font-size: 16px;"></i>
                <div>
                    <div class="font-weight-bold">Enable reminder emails to review this intervention</div>
                    <div class="text-muted mt-1" style="color:#495B8F">Emails include information about student progress, attendance, and participation in this intervention plan.</div>
                    <v-expand-transition>
                        <div v-show="reminder.enabled" class="mt-5">
                            <div class="text-muted">Send {{ reminderPerson }} reminder emails:</div>
                            <v-radio-group :dense="true" v-model="reminder.frequency" class="mt-2 intervention-creation-reminder-radio-group">
                                <v-radio data-test="intervention-creation-reminder-frequency-monthly" class="intervention-creation-reminder-radio" value="MONTH" style="height:35px;">
                                    <template v-slot:label>
                                        <span style="font-size: 14px;">Monthly</span> 
                                        <v-expand-x-transition>
                                            <div v-if="reminder.frequency === 'MONTH'" class="d-flex">
                                                <div class="ml-1 align-self-center" style="font-size: 14px;"> on</div>
                                                <fe-remote-combo data-test="intervention-creation-reminder-day-of-month" :clearable="false" :hide-details="true" :byId="true" v-model="reminder.dayOfMonth" :items="daysOfMonth" class="ml-3 intervention-creation-reminder-selection" style="width: 100px;" />
                                            </div>
                                        </v-expand-x-transition>
                                    </template>
                                </v-radio>
                                <v-radio data-test="intervention-creation-reminder-frequency-weekly" class="intervention-creation-reminder-radio" value="WEEK" style="height:35px;">
                                    <template v-slot:label>
                                        <span style="font-size: 14px;">Weekly</span> 
                                        <v-expand-x-transition><div v-if="reminder.frequency === 'WEEK'" class="d-flex"><div class="ml-1 align-self-center" style="font-size: 14px;">every</div>
                                            <fe-remote-combo data-test="intervention-creation-reminder-weekly-interval" :clearable="false" :hide-details="true" :byId="true" v-model="reminder.weeklyInterval" :items="weeklyIntervals" class="ml-3 intervention-creation-reminder-selection" style="width: 120px;" />
                                            <div class="align-self-center" style="font-size: 14px;">on</div>
                                            <fe-remote-combo data-test="intervention-creation-reminder-day-of-week" :clearable="false" :hide-details="true" :byId="true" v-model="reminder.dayOfWeek" :items="daysOfWeek" class="ml-3 intervention-creation-reminder-selection" style="width: 120px;" />
                                        </div>
                                    </v-expand-x-transition>
                                </template>
                            </v-radio>
                            </v-radio-group>
                        </div>
                    </v-expand-transition>
                </div>
                <div>
                    <div><fe-switch data-test="create-intervention-reminder-enabled" v-model="reminder.enabled" class="ms-auto" :hide-details="true" /></div>
                </div>
            </div>
            <div style="position:relative;"><v-progress-linear style="position:absolute;top:-10px;" class="mt-5" :indeterminate="true" v-if="reminderSlowSave" /></div>
        </div>
        <div class="text-center" style="margin-top: 32px;">
            <div class="text-left d-inline-block">
                <div class="mb-2" style="font-size:16px; font-weight: 500">What would you like to do next?</div>
                <div class="ma-5">
                    <a data-test="intervention-creation-view-intervention" class="action-link" @click="viewIntervention">
                        View Intervention<v-icon color="#006C90" size="12px" class="ml-2">far fa-arrow-right</v-icon>
                    </a>
                </div>
                <div class="ma-5">
                    <a data-test="intervention-creation-create-another-intervention" class="action-link" @click="$emit('createNew')">
                        Create Another Intervention<v-icon color="#006C90" size="12px" class="ml-2">far fa-arrow-right</v-icon>
                    </a>
                </div>
                <div class="ma-5">
                    <a :class="{'action-link-disabled': generatingReport}" data-test="intervention-creation-generate-report" class="action-link" @click="generateReport">
                        Generate Intervention Report{{ students.length !== 1 ? 's' : '' }}<v-icon color="#006C90" size="12px" class="ml-2">far fa-arrow-right</v-icon>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AlertIcon from '@/components/common/AlertIcon.vue'

export default {
    name: 'Completed',

    components: {
        AlertIcon,
    },

    props: {
        interventionist: {
            type: Object,
            required: true,
        },
        interventionName: {
            type: String,
            required: true,
        },
        interventionPlanId: {
            type: Number,
            required: true,
        },
        reminderAvailable: {
            type: Boolean,
            required: true,
        },
        students: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            reminderSlowSave: false,
            reminder: {
              enabled: false,
              frequency: 'MONTH',
              dayOfMonth: 1,
              weeklyInterval: 1,
              dayOfWeek: 'MON',
            },
            generatingReport: false,
        }
    },

    computed: {
        daysOfWeek() {
            return [
                {id: 'MON', name: 'Monday'},
                {id: 'TUE', name: 'Tuesday'},
                {id: 'WED', name: 'Wednesday'},
                {id: 'THU', name: 'Thursday'},
                {id: 'FRI', name: 'Friday'},
            ]
        },
        daysOfMonth() {
            return Array.from({length: 31}, (_, i) => ({id: i + 1, name: `Day ${i + 1}`}))
        },
        weeklyIntervals() {
            return Array.from({length: 52}, (_, i) => ({id: i + 1, name: `${i+1} week${i === 0 ? '' : 's'}`}))
        },
        reminderPerson() {
            return `${this.interventionist.fname} ${this.interventionist.lname}`.trim() || 'the interventionist'
        }
    },

    watch: {
        reminder: {
            handler() {
                let promises = []
                if (!this.reminder.enabled) {
                    promises.push(
                        this.$axios.delete(this.$models.getUrl('interventionReminders', 'resource', {id: this.interventionPlanId})).then(response => {
                            if (!this.$ecResponse(response)) {
                                return
                            }
                        })
                    )
                } else {
                    let payload = {frequency: this.reminder.frequency}
                    if (this.reminder.frequency === 'MONTH') {
                        payload.day_of_month = this.reminder.dayOfMonth
                    } else if (this.reminder.frequency === 'WEEK') {
                        payload.weekly_interval = this.reminder.weeklyInterval
                        payload.day_of_week = this.reminder.dayOfWeek
                    }
                    promises.push(
                        this.$axios.put(this.$models.getUrl('interventionReminders', 'resource', {id: this.interventionPlanId}), payload).then(response => {
                            if (!this.$ecResponse(response)) {
                                return
                            }
                        })
                    )
                }

                promises.push(new Promise(resolve => setTimeout(() => {
                    this.reminderSlowSave = true // if the request is slow, we need to show the progress bar
                    resolve()
                }, 1000)))

                Promise.all(promises).finally(() => {
                    this.reminderSlowSave = false // once the request is done, we need to hide the progress bar
                })
            },
            deep: true,
        },
    },

    mounted() {
        this.$store.commit('intervention/set', {
            key: 'interventionCompleted',
            value: true
        })
    },

    methods: {
        viewIntervention() {
            this.$emit('close')
            this.$store.commit('global/addDockableWindow', {
                name: 'Intervention',
                component: 'intervention-workspace',
                attrs: {interventionPlanId: this.interventionPlanId}
            })
        },
        generateReport() {
            if (this.generatingReport) {
                return
            }
            this.generatingReport = true
            this.$axios.post(this.$models.getUrl('interventionCreationReport', 'queue', {id: this.interventionPlanId})).then(response => {
                this.$root.$emit('waiterDialog', {
                    action: 'create',
                    text: 'Preparing Report' + (this.students.length !== 1 ? 's' : ''),
                    successText: 'Your report' + (this.students.length !== 1 ? 's are' : ' is') + ' ready',
                    batch_job_id: response.data.batch_job_id,
                    content_count: this.students.length,
                })
            })
        },
    }
}
</script>

<style lang="scss" scoped>
.action-link {
    color: #006C90;
    font-size: 14px;
    text-decoration: none;
}

.action-link-disabled {
    color: #9297A6;
    pointer-events: none;
}

.intervention-creation-reminder-radio-group {
    ::v-deep .v-input__slot {
        padding-left: 0 !important;
        margin-left: -5px !important;
    }
}

.intervention-creation-reminder-selection {
    ::v-deep .v-input__slot {
        padding-left: 8px !important;
    }
}
</style>